import React from 'react';
import { ADMIN_MESSAGE_FORMATS } from '../../../../constants/messageFormats';
import {
  buildSystemRichDescriptor,
  buildSystemTextDescriptor,
  renderSystemDescriptorCard,
} from './systemMessageUtils';
import { EVENT_FINISHED_CHAT_TITLE } from './eventMessageFormatUtils';

export const parseEventTimetableRuntimePayload = (message) => {
  if (!message) return null;
  const raw = message.content;
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  const text = String(raw || '').trim();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

export const resolveEventTimetableRuntimeVisibility = (message, payload) => {
  const format = String(message?.message_format || '');
  if (
    format === ADMIN_MESSAGE_FORMATS.TIMETABLE_MEMO_ADMIN ||
    format === ADMIN_MESSAGE_FORMATS.TIMETABLE_ANNOUNCEMENT_ADMIN ||
    format === 'timetable_memo_admin' ||
    format === 'timetable_announcement_admin'
  ) {
    return 'admin';
  }
  const visibility = String(payload?.visibility || '').toLowerCase();
  return visibility === 'admin' ? 'admin' : 'all';
};

export const buildEventAnnouncementChecks = (checks = {}) => ({
  isNoteCreateMessage: checks.isNoteCreateMessage,
  isNoteUpdateMessage: checks.isNoteUpdateMessage,
  isNoteDeleteMessage: checks.isNoteDeleteMessage,
  isTimetableMemoMessage: checks.isTimetableMemoMessage,
  isEventInfoPublishedMessage: checks.isEventInfoPublishedMessage,
  isEventMembersPublishedMessage: checks.isEventMembersPublishedMessage,
  isStageSeatingSharedMessage: checks.isStageSeatingSharedMessage,
  isEventInfoUpdatedMessage: checks.isEventInfoUpdatedMessage,
  isEventFinishedMessage: checks.isEventFinishedMessage,
  isEventMaterialSharedMessage: checks.isEventMaterialSharedMessage,
  isEventImagesAddedMessage: checks.isEventImagesAddedMessage,
  isTimetableCreatedMessage: checks.isTimetableCreatedMessage,
  isTimetableUpdatedMessage: checks.isTimetableUpdatedMessage,
  isTimetableRuntimeMessage: checks.isTimetableRuntimeMessage,
  isNoteMessage: checks.isNoteMessage,
});

export const resolveEventAnnouncementScopeLabel = (message) => {
  const performanceType = String(message?.source_performance_type || '').trim();
  if (performanceType) return performanceType;
  return String(message?.source_event_name || '').trim();
};

export const withEventAnnouncementScopeTitle = ({
  title,
  message,
  isGroupUnified = false,
}) => {
  const baseTitle = String(title || '').trim();
  if (!baseTitle) return baseTitle;
  if (!isGroupUnified) return baseTitle;
  const scopeLabel = resolveEventAnnouncementScopeLabel(message);
  if (!scopeLabel) return baseTitle;
  const prefix = `【${scopeLabel}】`;
  if (baseTitle.startsWith(prefix)) return baseTitle;
  return `${prefix}${baseTitle}`;
};

const resolveAnnouncementTone = (message, checks = {}) => {
  const format = String(message?.message_format || '').trim();
  if (format === ADMIN_MESSAGE_FORMATS.EVENT_CANCELLED || format === 'event_cancelled') {
    return 'cancelled';
  }
  if (checks.isEventInfoPublishedMessage?.(message)) return 'publish';
  if (checks.isEventMembersPublishedMessage?.(message)) return 'publish';
  if (checks.isStageSeatingSharedMessage?.(message)) return 'stage-seating';
  if (checks.isEventInfoUpdatedMessage?.(message)) return 'info';
  if (checks.isEventFinishedMessage?.(message)) return 'info';
  if (checks.isEventMaterialSharedMessage?.(message)) return 'material';
  if (checks.isEventImagesAddedMessage?.(message)) return 'album';
  if (
    checks.isTimetableCreatedMessage?.(message) ||
    checks.isTimetableUpdatedMessage?.(message) ||
    checks.isTimetableRuntimeMessage?.(message)
  ) {
    return 'timetable';
  }
  if (checks.isNoteMessage?.(message)) return 'note';
  return '';
};

export const resolveEventAnnouncementTitle = (message, checks = {}) => {
  if (checks.isNoteCreateMessage?.(message)) return 'ノートが作成されました';
  if (checks.isNoteUpdateMessage?.(message)) return 'ノートが更新されました';
  if (checks.isNoteDeleteMessage?.(message)) return 'ノートが削除されました';
  if (checks.isTimetableCreatedMessage?.(message)) return 'タイムテーブルが作成されました';
  if (checks.isTimetableUpdatedMessage?.(message)) return 'タイムテーブルが更新されました';
  if (checks.isEventMaterialSharedMessage?.(message)) return '共有資料が共有されました';
  if (checks.isEventImagesAddedMessage?.(message)) return 'イベントアルバムが共有されました';
  if (checks.isStageSeatingSharedMessage?.(message)) return 'ステージシーティング公開';
  if (checks.isEventInfoUpdatedMessage?.(message)) return 'イベント情報が更新されました';
  if (checks.isEventInfoPublishedMessage?.(message)) return 'イベント情報が公開されました';
  if (checks.isEventMembersPublishedMessage?.(message)) return 'メンバー公開';
  if (checks.isEventFinishedMessage?.(message)) return EVENT_FINISHED_CHAT_TITLE;
  return 'お知らせ';
};

export const resolveEventTimetableRuntimeTitle = (message, checks = {}) => {
  if (checks.isTimetableMemoMessage?.(message)) return 'タイムテーブル行メモ';
  return 'タイムテーブルのお知らせ';
};

export const getEventAnnouncementCardClass = (message, checks = {}) => {
  const tone = resolveAnnouncementTone(message, checks);
  return tone ? `system-message-card--${tone}` : '';
};

export const getEventAnnouncementToneClass = (message, checks = {}) => {
  const tone = resolveAnnouncementTone(message, checks);
  return tone ? `announce-tone--${tone}` : '';
};

export const buildEventAnnouncementDescriptor = ({
  message,
  extra = null,
  options = {},
  resolveAnnouncementTitle,
  withAnnouncementScopeTitle,
  getAnnouncementCardClass,
  getAnnouncementIconClass,
  linkifyText,
}) => {
  const title = withAnnouncementScopeTitle(
    options.title || resolveAnnouncementTitle(message),
    message,
  );
  const content = String(options.content ?? message?.content ?? '').trim();
  const showIcon = Boolean(options.showIcon);
  const iconClass = options.iconClass || getAnnouncementIconClass(message);
  const badge = options.badge || '';
  return buildSystemTextDescriptor({
    title,
    content: content ? linkifyText(content) : null,
    bodyExtra: options.bodyExtra || null,
    extra: extra ? <div className="system-message-card__actions">{extra}</div> : null,
    showIcon,
    iconClass,
    badge,
    cardClass: getAnnouncementCardClass(message),
  });
};

export const renderEventAnnouncementCard = (props) =>
  renderSystemDescriptorCard(buildEventAnnouncementDescriptor(props));

export const buildEventTimetableRuntimeDescriptor = ({
  message,
  parseTimetableRuntimePayload,
  isTimetableMemoMessage,
  resolveTimetableRuntimeVisibility,
  selectedEventId,
  resolveTimetableRuntimeTitle,
  withAnnouncementScopeTitle,
  getAnnouncementIconClass,
  linkifyText,
  buildDetailUrl,
}) => {
  if (!message) return buildSystemRichDescriptor();
  const payload = parseTimetableRuntimePayload(message) || {};
  const isMemo = isTimetableMemoMessage(message);
  const visibility = resolveTimetableRuntimeVisibility(message, payload);
  const isAdminOnly = visibility === 'admin';
  const rowTime = String(payload?.row_time || '').trim();
  const rowItem = String(payload?.row_item || payload?.row_label || '').trim();
  const rowId = String(payload?.row_id || '').trim();
  const contentText = String(payload?.content || '').trim();
  const rawText = typeof message.content === 'string' ? message.content.trim() : '';
  const fallbackText = rawText && !rawText.startsWith('{') ? rawText : '';
  const displayText = contentText || fallbackText;
  const timetableId = payload?.timetable_id ?? payload?.timetableId ?? null;
  const detailUrl =
    typeof buildDetailUrl === 'function'
      ? buildDetailUrl({ message, payload, selectedEventId, timetableId })
      : '';

  const title = withAnnouncementScopeTitle(resolveTimetableRuntimeTitle(message), message);
  const rowLabel = rowItem || (rowId ? `対象行: ${rowId}` : '');
  const showRow = isMemo && (rowTime || rowLabel);
  return buildSystemRichDescriptor({
    title,
    content: (
      <>
        {showRow ? (
          <div className="system-message-card__row">
            {rowTime ? <span className="system-message-card__row-time">{rowTime}</span> : null}
            {rowTime && rowLabel ? <span className="system-message-card__row-divider" /> : null}
            {rowLabel ? <span className="system-message-card__row-label">{rowLabel}</span> : null}
          </div>
        ) : null}
        <div className={`system-message-card__text${displayText ? '' : ' system-message-card__text--muted'}`}>
          {displayText ? linkifyText(displayText) : '内容がありません。'}
        </div>
        {detailUrl ? (
          <a
            className="system-message-card__link"
            href={detailUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            タイムテーブルを開く
          </a>
        ) : null}
      </>
    ),
    showIcon: false,
    iconClass: getAnnouncementIconClass(message),
    badge: isAdminOnly ? '管理者のみ' : '',
    cardClass: isMemo ? 'system-message-card--memo' : 'system-message-card--announcement',
  });
};

export const renderEventTimetableRuntimeCard = (props) =>
  renderSystemDescriptorCard(buildEventTimetableRuntimeDescriptor(props));

export const buildEventCancelledDescriptor = ({
  message,
  withAnnouncementScopeTitle,
  linkifyText,
}) => {
  const cancelledText = String(message?.content || '').trim();
  return buildSystemTextDescriptor({
    title: withAnnouncementScopeTitle('イベント中止のお知らせ', message),
    content: cancelledText ? linkifyText(cancelledText) : null,
    showIcon: false,
    iconClass: 'fa-ban',
    badge: '中止',
    cardClass: 'chat-bubble im-bubble system-message system-message--event-cancelled',
  });
};

export const renderEventCancelledCardBody = (props) =>
  renderSystemDescriptorCard(buildEventCancelledDescriptor(props));
